import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { RESIDENT, printDate, nextId } from '../../state/building.js';

import Retriever from '../../media/images/modules/pet-retriever.jpg';
import Ragdoll from '../../media/images/modules/pet-ragdoll.jpg';
import OtherPet from '../../media/images/modules/form-pet.jpg';

/* A photo for a pet by type, for ones registered here. */
const PHOTO = { Dog: Retriever, Cat: Ragdoll };

/* A date input's value, `offset` days from today. */
function iso(offset = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Days from today until a yyyy-mm-dd value. */
function daysUntil(value) {
    const [y, m, d] = value.split('-').map(Number);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((new Date(y, m - 1, d) - today) / 86400000);
}

function vaccineChip(value) {
    const days = daysUntil(value);
    if (days < 0) return <Chip tone="alert">Vaccination expired</Chip>;
    if (days <= 30) return <Chip tone="warn">Expires in {days} days</Chip>;
    return <Chip tone="ok">Vaccinations current</Chip>;
}

const SEED = [
    { id: 1, name: 'Mango', type: 'Dog', breed: 'Golden retriever', weight: 62, expires: iso(210), tag: 'P-1204-A', file: 'mango-rabies-2026.pdf', image: Retriever },
    { id: 2, name: 'Coco', type: 'Cat', breed: 'Ragdoll', weight: 11, expires: iso(18), tag: 'P-1204-B', file: 'coco-vaccines.jpg', image: Ragdoll }
];

/* Pet Registration: the resident's registered pets and a form to add one. */
function PetsScreen() {
    const [flash, notify] = useFlash();
    const [pets, setPets] = useState(SEED);

    const submit = event => {
        const file = event.currentTarget.elements.record.files[0];
        const values = readForm(event, ['name', 'breed', 'expires']);
        if (!values) return notify('Add a name, breed and vaccination date first.');

        setPets(list => [...list, {
            id: nextId(),
            name: values.name,
            type: values.type,
            breed: values.breed,
            weight: Number(values.weight) || 0,
            expires: values.expires,
            tag: `P-${RESIDENT.unit}-${String.fromCharCode(65 + list.length)}`,
            file: file ? file.name : '',
            image: PHOTO[values.type] || OtherPet
        }]);
        notify(`${values.name} is registered. Management will review the records within one business day.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Pet registration" title="Your pets" />
            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Register a pet</h4>
                    <div className="form">
                        <Field label="Name">
                            <input name="name" className="form-control" placeholder="e.g. Bruno" />
                        </Field>
                        <Field label="Type">
                            <select name="type" className="form-control" defaultValue="Dog">
                                {['Dog', 'Cat', 'Bird', 'Other'].map(t => <option key={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Breed">
                            <input name="breed" className="form-control" placeholder="e.g. French bulldog" />
                        </Field>
                        <Field label="Weight (lb)">
                            <input name="weight" type="number" min="0" className="form-control" placeholder="25" />
                        </Field>
                        <Field label="Rabies vaccination expires" wide>
                            <input name="expires" type="date" className="form-control" defaultValue={iso(365)} />
                        </Field>
                        <Field label="Vaccination record (photo or PDF)" wide>
                            <input name="record" type="file" className="form-control" accept="image/*,.pdf" />
                        </Field>
                        <p className="row__meta field--wide">Building rules allow two pets per unit, dogs up to 80 lb. Pets must be leashed in common areas.</p>
                        <button type="submit" className="btn btn-primary">Register pet</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Registered pets</h4>
                    {pets.length === 0 ? <Empty>No pets registered.</Empty> : (
                        <div className="cards">
                            {pets.map((p, i) => (
                                <Panel key={p.id} index={i}>
                                    <img className="panel__media" src={p.image} alt={`${p.name}, ${p.breed.toLowerCase()}`} loading="lazy" />
                                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                        <Chip tone="muted">{p.type} · tag {p.tag}</Chip>
                                        {vaccineChip(p.expires)}
                                    </div>
                                    <h4>{p.name}</h4>
                                    <p className="row__meta">
                                        {p.breed} · {p.weight} lb · vaccination expires {printDate(p.expires)}
                                        {p.file ? ` · ${p.file}` : ' · no record uploaded'}
                                    </p>
                                </Panel>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PetsScreen;
